import React, {useEffect, useState} from 'react';
import {Globe, Mail, Menu, Phone, X} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {useLanguage} from '@/contexts/LanguageContext';
import {smoothScrollTo} from '@/utils/smoothScroll';
import {useIsMobile} from "@/hooks/use-mobile";
import {trackContactClick} from '@/lib/analytics';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const {language, setLanguage, t} = useLanguage();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, {passive: true});
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const navItems = [
        {name: t('nav.home'), href: '#home'},
        {name: t('nav.services'), href: '#services'},
        {name: t('nav.about'), href: '#about'},
        {name: t('nav.reviews'), href: '#reviews'},
        {name: t('nav.contact'), href: '#contact'}
    ];

    const toggleLanguage = () => {
        setLanguage(language === 'en' ? 'uk' : 'en');
    };

    return (
        <>
            {/* Top Bar (not sticky) */}
            <div className="bg-navy text-white py-2 px-4">
                <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center text-sm">
                    <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3 sm:gap-4 w-full sm:w-auto mb-1 sm:mb-0">

                        {/* PHONE */}
                        <a
                            href="tel:+380639884342"
                            onClick={() => trackContactClick('phone')}
                            className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium hover:bg-white/20 transition-colors duration-200 whitespace-nowrap"
                        >
                            <Phone className="w-4 h-4 text-primary align-middle" />
                            {t('contact.phone')}
                        </a>

                        {/* EMAIL */}
                        <a
                            href={`mailto:${t('contact.email')}`}
                            className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium hover:bg-white/20 transition-colors duration-200 whitespace-nowrap"
                        >
                            <Mail className="w-4 h-4 text-primary align-middle" />
                            {t('contact.email')}
                        </a>

                    </div>

                    {/* GLOBE (large only) */}
                    <div className="hidden lg:flex items-center gap-1 bg-white/10 px-3 py-1.5 rounded-full">
                        <Globe className="w-4 h-4 text-primary" />
                        <span className="text-xs md:text-sm font-medium">
                            {t('contact.global')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Sticky Navigation */}
            <header
                className={`sticky top-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? 'bg-white/85 backdrop-blur-lg shadow-lg'
                        : 'bg-white shadow-sm'
                }`}
            >
                <nav className="container mx-auto px-4 py-2.5 md:py-3">
                    <div className="flex justify-between items-center">
                        {/* Logo */}
                        <a
                            href="#home"
                            className="flex items-center gap-2.5"
                            onClick={(e) => {
                                e.preventDefault();
                                smoothScrollTo('home');
                            }}
                        >
                            <img
                                src="/images/logo.png"
                                alt="NK Cargo"
                                className="h-11 md:h-14 w-auto object-contain mix-blend-multiply"
                                width={160}
                                height={56}
                                loading="eager"
                                fetchPriority="high"
                            />
                            <span className="text-xl md:text-2xl font-extrabold tracking-tight text-navy leading-none">
                                NK <span className="text-primary">Cargo</span>
                            </span>
                        </a>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
                            {navItems.map((item) => (
                                <a
                                    key={item.name}
                                    href={item.href}
                                    className="text-gray-700 hover:text-primary transition-colors duration-200 font-medium relative group"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const elementId = item.href.substring(1); // Remove the # from the href
                                        smoothScrollTo(elementId);
                                    }}
                                >
                                    {item.name}
                                    <span
                                        className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
                                </a>
                            ))}

                            {/* Language Switcher */}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={toggleLanguage}
                                className="flex items-center space-x-1 hover:bg-gray-100 transition-colors duration-200"
                            >
                                <img
                                    src={language === 'en' ? '/uk-flag.svg' : '/ukraine-flag.svg'}
                                    alt={language === 'en' ? 'English' : 'Ukrainian'}
                                    className="w-5 h-3 object-cover"
                                    width={20}
                                    height={12}
                                    loading="eager"
                                />
                                <span className="font-medium">{language.toUpperCase()}</span>
                            </Button>

                            <Button
                                className="bg-primary hover:bg-primary/90 text-white px-8 py-1.5 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                                onClick={() => {
                                    smoothScrollTo('contact');
                                }}
                            >
                                {t('nav.getQuote')}
                            </Button>
                        </div>

                        {/* Mobile Menu Button */}
                        {useIsMobile() && <div className="flex items-center space-x-2 lg:hidden">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={toggleLanguage}
                                className="flex items-center space-x-1 hover:bg-gray-100 transition-colors duration-200"
                            >
                                <img
                                    src={language === 'en' ? '/uk-flag.svg' : '/ukraine-flag.svg'}
                                    alt={language === 'en' ? 'English' : 'Ukrainian'}
                                    className="w-5 h-3 object-cover"
                                    width={20}
                                    height={12}
                                    loading="eager"
                                />
                                <span className="text-sm font-medium">{language.toUpperCase()}</span>
                            </Button>

                            <button
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                            >
                                {isMenuOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
                            </button>
                        </div>}
                    </div>

                    {/* Mobile Navigation */}
                    <div className={`lg:hidden transition-all duration-200 ease-in-out ${
                        isMenuOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0 overflow-hidden'
                    }`}>
                        <div className="py-4 border-t border-gray-200">
                            <div className="flex flex-col space-y-3">
                                {navItems.map((item) => (
                                    <a
                                        key={item.name}
                                        href={item.href}
                                        className="text-gray-700 hover:text-primary transition-colors duration-200 font-medium py-2 px-2 rounded-lg hover:bg-gray-50"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsMenuOpen(false);
                                            const elementId = item.href.substring(1); // Remove the # from the href
                                            smoothScrollTo(elementId);
                                        }}
                                    >
                                        {item.name}
                                    </a>
                                ))}
                                <Button
                                    className="bg-primary hover:bg-primary/90 text-white w-full py-1.5 mt-4 rounded-full transition-all duration-200"
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        smoothScrollTo('contact');
                                    }}
                                >
                                    {t('nav.getQuote')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>
        </>
    );
};

export default Header;
